import { cva, type VariantProps } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../../lib/cn'

/**
 * The Status set (210:17612). Its eight Figma variants are work-order statuses, and
 * those are data, configured per workflow, so the component takes the visual tone
 * and the label separately. The variants map to tones as:
 *
 * - neutral: Default (labelled "Backlog")
 * - warning: Initial Check
 * - accent: In Progress
 * - purple: Internal Validation
 * - error: External Validation
 * - success: Generate Distribution Package, Done, Ready to use
 *
 * `size="md"` is the pill the Work Order Stage Card draws (498:8514): the same
 * shape with its text overridden to Body SemiBold 16/25.
 *
 * The pill hugs its label and does not shrink, so a long label renders in full at
 * its natural width, as the Figma draws "Generate Distribution Package" (225px) even
 * where it overruns its table cell's padding (321:25618). The label is set to clip
 * with an ellipsis, as the Figma's text is, which only shows when a caller caps
 * the pill's width.
 */
export const statusVariants = cva('inline-flex shrink-0 items-center rounded-atlas-sm px-3 py-0.5 font-atlas-sans', {
	variants: {
		size: {
			sm: 'text-atlas-label-semibold',
			md: 'text-atlas-body-semibold',
		},
		tone: {
			neutral: 'bg-atlas-background text-atlas-light-dark',
			warning: 'bg-atlas-warning-light text-atlas-warning',
			accent: 'bg-atlas-accent-light text-atlas-accent',
			purple: 'bg-atlas-status-purple-light text-atlas-status-purple',
			error: 'bg-atlas-error-light text-atlas-error',
			success: 'bg-atlas-success-light text-atlas-success',
		},
	},
	defaultVariants: { size: 'sm', tone: 'neutral' },
})

/** The colour pairs a Status can take, named for their meaning on the Colors board. */
export type StatusTone = NonNullable<VariantProps<typeof statusVariants>['tone']>

export interface StatusProps
	extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'>,
		VariantProps<typeof statusVariants> {
	/** The status's display text, e.g. the workflow status's configured label. */
	label: string
}

/** A tinted status pill at its label's natural width. */
export function Status({ label, tone, size, className, ...props }: StatusProps) {
	return (
		<span {...props} className={cn(statusVariants({ size, tone }), className)}>
			<span className="truncate">{label}</span>
		</span>
	)
}
